import React, { Children, createElement, CSSProperties, isValidElement, MouseEvent, PureComponent, ReactElement } from 'react';

import { ButtonAPI } from '@philips-emr/tws-components-api';
import { ButtonTheme, ButtonType, Size } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, bool, color, element, eventListener, expression, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, defaultClasses, enumValues, isEnumValid, isFunction, trigger } from '@philips-emr/tws-utils';

import { withRouterContext } from '..';
import { RouterContextProps } from '../with-router-context/with-router-context';

import './button.css';

export interface ButtonProperties {
  background?: string;
  badge?: string;
  bold?: boolean;
  children?: string | string[];
  className?: string;
  color?: string;
  enabled: boolean;
  icon?: string | ReactElement;
  iconClassName?: string;
  onBlur?: () => void;
  onClick: (event: ClickEvent) => void;
  onContextMenuClick?: () => void;
  onDoubleClick?: () => void;
  onFocus?: () => void;
  preventDefault?: boolean;
  size?: Size.NONE | Size.SMALL | Size.LARGE;
  stopPropagation?: boolean;
  theme?: ButtonTheme;
  type?: ButtonType;
  value: string;
}

// TODO: change type when we have all type definitions.
@AwesomeComponent({ type: ComponentType.NAVIGATION, api: ButtonAPI })
class Button extends PureComponent<ButtonProperties> {
  static propTypes = {
    background: string,
    badge: string,
    bold: bool,
    children: oneOfType([string, arrayOf(string)]).notEnumerable(),
    className: string,
    color: color,
    enabled: bool,
    icon: oneOfType([string, element]),
    iconClassName: string,
    onBlur: eventListener,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onFocus: eventListener,
    preventDefault: bool.group('events'),
    size: oneOf([Size.NONE, Size.SMALL, Size.LARGE]),
    stopPropagation: bool.group('events'),
    theme: oneOf(enumValues(ButtonTheme)),
    type: oneOf(enumValues(ButtonType)),
    value: expression,
  };

  static defaultProps = {
    enabled: true,
    value: 'Button',
    type: ButtonType.BUTTON,
    theme: ButtonTheme.PRIMARY,
  };

  onClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const { onClick, preventDefault, stopPropagation } = this.props;

    if (preventDefault) {
      event.preventDefault();
    }

    if (stopPropagation) {
      event.stopPropagation();
    }

    if (isFunction(onClick)) {
      onClick(new ClickEvent({
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation(),
      }));
    }
  };

  getButtonClasses(): string {
    const { bold, className, enabled, size, theme } = this.props;

    return classNames({
      'c-button': true,
      'u-text-bold': Boolean(bold),
      [`${className}`]: Boolean(className),
      [`c-button--${size}`]: isEnumValid(Size, size),
      [`c-button--${theme}`]: isEnumValid(ButtonTheme, theme),
      ...defaultClasses({ enabled }),
    });
  }

  getButtonStyle(): CSSProperties {
    const { background, color } = this.props;

    return { background, color };
  }

  renderIcon(): ReactElement | null {
    const { icon, iconClassName } = this.props;

    if (!icon) {
      return null;
    }

    if (isValidElement(icon)) {
      const oldClassName = icon.props.className ?? '' ;
      const newClassName = `c-button__icon ${oldClassName}`;

      return createElement(icon.type, { ...icon.props, className: newClassName });
    }

    const imageComponent = ({ href }: RouterContextProps): ReactElement => {
      const className = classNames({
        'c-button__icon': true,
        [`${iconClassName}`]: Boolean(iconClassName),
      });

      return <img className={className} src={href} />;
    };

    const ImageElement = withRouterContext(imageComponent, icon);

    return <ImageElement />;
  }

  getTextValue(): string {
    const { children, value } = this.props;

    if (Children.count(children) === 1 && typeof children === 'string') {
      return children;
    }

    return msg(value);
  }

  render(): ReactElement {
    const { badge, enabled, type, onBlur, onContextMenuClick, onDoubleClick, onFocus } = this.props;

    return (
      <button
        className={this.getButtonClasses()}
        data-badge={badge}
        disabled={!enabled}
        onBlur={trigger(onBlur)}
        onClick={(event) => this.onClick(event)}
        onContextMenu={trigger(onContextMenuClick)}
        onDoubleClick={trigger(onDoubleClick)}
        onFocus={trigger(onFocus)}
        style={this.getButtonStyle()}
        type={type}
        x-contrast="bg"
      >
        {this.renderIcon()}
        <span className="c-button__text" x-contrast="cl">
          {this.getTextValue()}
        </span>
      </button>
    );
  }
}

export default Button;
